/**
 * The wallet, for every screen that shows it.
 *
 * A context beside `MoneyContext` and for the same reason: the saloon header, the room
 * header, the felt's gauge and the Play cards all show the same balance, and a screen
 * where two of them disagree is a screen nobody trusts about money — even play money.
 *
 * ONE INTERFACE, TWO WALLETS. Signed in, the balance is the server's and every change
 * comes back from it. Signed out, it is the device wallet in `lib/wallet-client.ts`,
 * running the same rules locally. Nothing above this line knows which it is looking
 * at, except the one place that has to: the copy that tells a guest their Bucks live
 * on this device and that signing in starts them at 5,000.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode,
} from "react";
import { useAuth } from "@clerk/clerk-react";
import { api, type LedgerEntry } from "./api";
import { BucksIcon } from "./mark";
import {
  readGuestWallet, guestPost, guestStake, clearGuestWallet,
  STAKE_FLOOR, STAKE_TO, STARTING_GRANT,
} from "./wallet-client";

export interface WalletState {
  balance: number;
  /** What this visit has done to it. Positive up, negative down, zero draws nothing. */
  session: number;
  /** Whether this is the device wallet. Drives one line of copy and nothing else. */
  guest: boolean;
  loading: boolean;
  /** Would the Barkeep say yes right now? The rule is his, not the caller's. */
  canStake: boolean;
  floor: number;
  stakeTo: number;
  recent: LedgerEntry[];
  refresh: () => Promise<void>;
  stake: () => Promise<void>;
  /**
   * Tell the wallet what a settlement just did, without waiting for a round trip.
   *
   * The table knows the new balance the moment the settlement replies, and a header
   * that says the old number until the next fetch is a header that is wrong at exactly
   * the moment the player is looking at it.
   */
  applied: (balance: number, sessionDelta?: number) => void;
  /** A guest's local debit and credit, so the same table code drives both wallets. */
  spendLocal: (amount: number) => boolean;
  creditLocal: (amount: number) => void;
}

const Ctx = createContext<WalletState | null>(null);

export function WalletProvider({ children }: { children: ReactNode }) {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [balance, setBalance] = useState(() => (isSignedIn ? 0 : readGuestWallet().balance));
  const [recent, setRecent] = useState<LedgerEntry[]>([]);
  const [session, setSession] = useState(0);
  const [loading, setLoading] = useState(true);

  /** Clerk's `getToken` is a new closure every render; see the note in `useMe.ts`. */
  const tokenRef = useRef(getToken);
  tokenRef.current = getToken;

  /**
   * The device wallet is cleared the first time somebody signs in.
   *
   * Not added to the grant — `wallet-client.ts` explains at length why a balance that
   * survives a sign-up is a balance anybody can manufacture. Clearing it here rather
   * than in the sign-in flow means it happens however they arrive: the button, a link,
   * a session restored in another tab.
   */
  useEffect(() => { if (isSignedIn) clearGuestWallet(); }, [isSignedIn]);

  const refresh = useCallback(async () => {
    if (!isSignedIn) {
      const w = readGuestWallet();
      setBalance(w.balance);
      setRecent(w.ledger.map((e) => ({
        id: `${e.at}:${e.delta}`, delta: e.delta, balance_after: e.balanceAfter,
        reason: e.reason, ref_id: null, created_at: Math.floor(e.at / 1000),
      })));
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const w = await api.wallet(tokenRef.current);
      setBalance(w.balance);
      setRecent(w.recent);
    } catch {
      /* A wallet that cannot be read is not a reason to take a screen down. The header
         keeps the last number it had, and the next navigation tries again. */
    } finally {
      setLoading(false);
    }
  }, [isSignedIn]);

  useEffect(() => { if (isLoaded) void refresh(); }, [isLoaded, refresh]);

  const stake = useCallback(async () => {
    if (!isSignedIn) {
      const w = guestStake();
      if (w) { setBalance(w.balance); void refresh(); }
      return;
    }
    try {
      const out = await api.stake(tokenRef.current);
      setBalance(out.balance);
      void refresh();
    } catch { /* the dialog stays open; the balance is unchanged and still true */ }
  }, [isSignedIn, refresh]);

  const applied = useCallback((next: number, delta = 0) => {
    setBalance(next);
    if (delta !== 0) setSession((s) => s + delta);
  }, []);

  const spendLocal = useCallback((amount: number) => {
    const w = guestPost(-Math.abs(Math.round(amount)), "buyin");
    if (!w) return false;
    setBalance(w.balance);
    setSession((s) => s - Math.abs(Math.round(amount)));
    return true;
  }, []);

  const creditLocal = useCallback((amount: number) => {
    const w = guestPost(Math.abs(Math.round(amount)), "settle");
    if (w) { setBalance(w.balance); setSession((s) => s + Math.abs(Math.round(amount))); }
  }, []);

  const value = useMemo<WalletState>(() => ({
    balance, session, guest: !isSignedIn, loading,
    canStake: balance < STAKE_FLOOR,
    floor: STAKE_FLOOR, stakeTo: STAKE_TO,
    recent, refresh, stake, applied, spendLocal, creditLocal,
  }), [balance, session, isSignedIn, loading, recent, refresh, stake, applied, spendLocal, creditLocal]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * The wallet, wherever it is needed.
 *
 * Falls back to a still, empty wallet outside a provider rather than throwing — the
 * harness renders single components on their own, and a gauge is not the right place
 * to take a page down.
 */
export function useWallet(): WalletState {
  const v = useContext(Ctx);
  if (v) return v;
  return {
    balance: STARTING_GRANT, session: 0, guest: true, loading: false,
    canStake: false, floor: STAKE_FLOOR, stakeTo: STAKE_TO, recent: [],
    refresh: async () => {}, stake: async () => {}, applied: () => {},
    spendLocal: () => true, creditLocal: () => {},
  };
}

/**
 * The gauge: the coin, the balance, and what this visit has done to it.
 *
 * Built in Phase 2 and kept hidden ever since, because a balance nobody had would have
 * been on every screenshot. This is the same element with a real number in it. The
 * session figure is drawn only when it is not zero — a "+0" beside a balance is a
 * decoration, and the gauge sits in a header a phone cannot spare pixels from.
 *
 * It draws the coin itself rather than going through `MoneyContext`: a balance is a
 * balance whichever unit somebody prefers to read a felt in, and the gauge is outside
 * any table's provider on three of the four screens it appears on.
 */
export function WalletGauge({ className = "", onStake }: { className?: string; onStake?: () => void }) {
  const { balance, session, canStake } = useWallet();
  const body = (
    <>
      <BucksIcon className="bucks-mark" />
      <span className="wg-bal num">{balance.toLocaleString("en-US")}</span>
      {session !== 0 && (
        <span className={`wg-ses num ${session > 0 ? "wg-up" : "wg-down"}`}>
          {session > 0 ? "+" : "−"}{Math.abs(session).toLocaleString("en-US")}
        </span>
      )}
    </>
  );
  const label = `${balance.toLocaleString("en-US")} Buster Bucks`
    + (session === 0 ? "" : `, ${session > 0 ? "up" : "down"} ${Math.abs(session).toLocaleString("en-US")} this visit`);

  /* Below the floor the gauge becomes the way to the Barkeep, because that is where
     somebody looks when they have run out — not in a settings sheet. */
  if (canStake && onStake) {
    return (
      <button type="button" className={`wallet-gauge wg-low ${className}`.trim()}
              onClick={onStake} aria-label={`${label}. Ask the Barkeep for a stake.`}>
        {body}
      </button>
    );
  }
  return <span className={`wallet-gauge ${className}`.trim()} aria-label={label}>{body}</span>;
}

/**
 * An amount of Bucks, with the coin, outside any table's provider.
 *
 * The lobby cards, the wallet page and the Barkeep all print prices, and none of them
 * is inside a `MoneyProvider` — nor should they be, because a price is not a property
 * of a table being played. `MoneyContext.amount()` stays the felt's way of asking.
 */
export function BucksAmount({ n }: { n: number }) {
  return (
    <span className="bucks-amount">
      <BucksIcon className="bucks-mark" />
      {n.toLocaleString("en-US")}
    </span>
  );
}
